// Where the console gets its configuration: one value, the gateway URL, decided
// by the hostname at runtime.
//
// ── No build-time settings ───────────────────────────────────────────────────
// Our server runs the sign-in (#385, see auth.ts), so the console reads no
// build-time variable at all. The three Auth0 settings it used to require —
// domain, client id, audience — belonged to the Auth0 single-page application it
// signed in with before. Nothing reads them now, so a build without them is
// complete.
//
// ── The gateway URL is RUNTIME, because it must vary and Vite cannot ─────────
// ⚠️ This is not a style preference; a build-time `VITE_API_BASE` is unshippable
// here. Cloudflare Pages offers exactly TWO variable scopes — Production and
// Preview — and Preview covers *every* non-production branch. So `qa` and
// `staging` are built from the same variable set, and one build-time value
// cannot be right for both: whichever gateway is chosen, the other environment's
// console silently talks to the wrong database. Nothing errors; it just reads
// someone else's customer list.
//
// `apps/fe` solved this in `src/env-config.js` by mapping hostname → gateway at
// runtime. Same problem, same answer, and doing it differently here would be the
// drift. The consequence worth stating: the console ships ONE artefact that is
// correct on all three hostnames, so a promotion cannot carry the wrong URL.
//
// ── Why the gateway list is COPIED, and how it is kept honest ────────────────
// The three URLs also live in `apps/fe/src/env-config.js`. That file is a
// classic-script IIFE that assigns `window.DYNAMO_ENV` and exports nothing, so
// there is no module to import — and `apps/fe` has no build step by design, so
// it cannot import from here either. Rather than let two copies drift silently,
// `env.test.ts` parses that file and asserts the two sets are equal. Same shape
// as `scripts/sync-design-system.mjs`: copy the value, guard it with a check
// that fails loudly, never a shared import neither app can express.

/** Configuration the console needs to boot. */
export interface ConsoleEnv {
  apiBase: string;
}

/**
 * Gateway base URL per environment.
 *
 * MUST stay equal to the `gatewayBase` values in `apps/fe/src/env-config.js` —
 * asserted by `env.test.ts`, so adding an environment in one app and not the
 * other fails the build rather than shipping.
 */
export const GATEWAY_BASES = {
  production: "https://gateway-production-2b3e.up.railway.app",
  qa: "https://gateway-qa.up.railway.app",
  staging: "https://gateway-staging-30ab.up.railway.app",
} as const;

export type EnvName = keyof typeof GATEWAY_BASES;

/**
 * Console hostname → which environment's gateway to call.
 *
 * A separate Cloudflare Pages project from the authoring app, so these are
 * `*.dynamo-console.pages.dev`, not `*.dynamo-authoring.pages.dev`. Production
 * branch `main` serves the apex; `qa` and `staging` are branch deploys.
 */
export const HOST_ENVIRONMENT: Readonly<Record<string, EnvName>> = {
  "dynamo-console.pages.dev": "production",
  "qa.dynamo-console.pages.dev": "qa",
  "staging.dynamo-console.pages.dev": "staging",
};

/**
 * Resolve the gateway for a hostname.
 *
 * ⚠️ An unrecognised host — localhost, a per-commit Pages preview
 * (`<hash>.dynamo-console.pages.dev`), a custom domain nobody has added here —
 * resolves to **QA, never production**. The asymmetry is deliberate and is the
 * same rule `apps/fe` applies: an unknown console must not be able to read the
 * production customer registry because someone forgot to add a hostname. The
 * failure mode of guessing QA is a confusing empty list; the failure mode of
 * guessing production is an operator acting on live customers from a URL nobody
 * meant to exist.
 */
export function resolveApiBase(hostname: string): string {
  const env = HOST_ENVIRONMENT[hostname];
  return GATEWAY_BASES[env ?? "qa"];
}

/**
 * The console's configuration. Nothing in it can be missing: the gateway comes
 * from the hostname, and an unrecognised host gets QA (`resolveApiBase`, above).
 */
export function readEnv(hostname: string = window.location.hostname): ConsoleEnv {
  return { apiBase: resolveApiBase(hostname) };
}
